import { useEffect, useMemo, useState } from "react";
import { tryApiGet } from "../api/client";
import { usePagedFetch } from "../api/usePagedFetch";
import { useAppData } from "../state/AppData";
import { colorFor } from '../lib/definitions/palette';
import { isProtocol, packetCategoryInfo, PROTOCOL_LABEL, type Protocol } from '../lib/definitions/protocols';
import { NO_CONNECTIONS_EMPTY_STATE, PAGE_SIZE } from '../lib/definitions/navigation';
import { trustTierInfo } from '../lib/definitions/trust';
import { relativeTime } from "../lib/format";
import { packetCursor, sourceChipText, SOURCE_FILTERS, type SourceFilter } from "../lib/packetFeed";
import { PagingBar } from "../components/PagingBar";
import { PacketsViewSwitch, packetsViewHash } from "../components/PacketsViewSwitch";
import { navigate } from "../lib/router";
import type { Packet, PacketTypeEntry } from "../api/types";

// The byte-level packet inspector: our own radios' and MQTT's packets and CoreScope's, as one list
// (backend api/packet_feed.py). A packet heard by more than one source is one row; its Source cell
// lists every source. Every filter runs on the server so it applies to the whole history, not to
// the page that happens to be loaded. Paging is by heard time with a (time, id) cursor.
type ProtocolTab = "all" | Protocol;
const PROTOCOL_TAB_ORDER: ProtocolTab[] = ["all", "meshtastic", "meshcore"];

function protocolTabLabel(tab: ProtocolTab): string {
  return tab === "all" ? "All" : PROTOCOL_LABEL[tab];
}

const EMPTY = "—";

export function PacketsPage() {
  const { effectiveTheme, select, availableProtocols, healthEverLoaded } = useAppData();
  const [protocolFilter, setProtocolFilter] = useState<ProtocolTab>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [typeEntries, setTypeEntries] = useState<PacketTypeEntry[]>([]);

  // The search box feeds the request after a short pause so each keystroke is not a query.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    tryApiGet<PacketTypeEntry[]>("/api/packets/types")
      .then((res) => !cancelled && setTypeEntries(Array.isArray(res) ? res : []))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Type buttons are categories; each stands for the raw types that fall in it (for the chosen protocol).
  const typeOptions = useMemo(() => {
    const map = new Map<string, { label: string; raw: Set<string> }>();
    for (const e of typeEntries) {
      if (protocolFilter !== "all" && e.protocol !== protocolFilter) continue;
      const cat = packetCategoryInfo(e.protocol, e.type);
      const entry = map.get(cat.key) ?? { label: cat.label, raw: new Set<string>() };
      entry.raw.add(e.type);
      map.set(cat.key, entry);
    }
    return Array.from(map.entries()).sort((a, b) => a[1].label.localeCompare(b[1].label));
  }, [typeEntries, protocolFilter]);

  const activeType = typeOptions.find(([key]) => key === typeFilter);

  const params: Record<string, string> = {};
  if (protocolFilter !== "all") params.protocol = protocolFilter;
  if (activeType) params.type = Array.from(activeType[1].raw).join(",");
  if (sourceFilter !== "all") params.source = sourceFilter;
  if (query) params.q = query;

  const { data, page, hasPrev, hasNext, next, prev } = usePagedFetch<Packet[]>("/api/packets", params, PAGE_SIZE, {
    // A full page means there is probably more; the cursor is the last row's time and id.
    getNextCursor: (arr) => (Array.isArray(arr) && arr.length >= PAGE_SIZE ? packetCursor(arr[arr.length - 1]) : null),
    applyCursor: (search, cursor) => {
      const [before, beforeId] = cursor.split("|");
      search.set("before", before);
      if (beforeId) search.set("before_id", beforeId);
    },
    // Only the first page joins the persisted set, so a reload has rows on first paint.
    persistFirstPage: true,
  });

  const packets = Array.isArray(data) ? data : [];
  const rangeStart = packets.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + packets.length - 1;

  return (
    <div className="node-list-page">
      <h2 className="audit-page-title">Packets</h2>
      <PacketsViewSwitch active="feed" onSelect={(v) => navigate(packetsViewHash(v))} />

      <div className="theme-toggle" role="group" aria-label="Protocol" style={{ marginBottom: 10 }}>
        {PROTOCOL_TAB_ORDER.map((t) => (
          <button
            key={t}
            data-active={protocolFilter === t}
            onClick={() => {
              setProtocolFilter(t);
              setTypeFilter("all");
            }}
          >
            {protocolTabLabel(t)}
          </button>
        ))}
      </div>

      <div className="node-list-toolbar">
        <input className="node-list-search" placeholder="Search from/to id…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <span className="node-list-count">{packets.length} shown</span>
      </div>

      <div className="theme-toggle" role="group" aria-label="Type" style={{ marginBottom: 10, flexWrap: "wrap" }}>
        <button data-active={!activeType} onClick={() => setTypeFilter("all")}>
          All types
        </button>
        {typeOptions.map(([key, opt]) => (
          <button key={key} data-active={typeFilter === key} onClick={() => setTypeFilter(key)}>
            {opt.label}
          </button>
        ))}
      </div>

      <div className="theme-toggle" role="group" aria-label="Source" style={{ marginBottom: 10, flexWrap: "wrap" }}>
        {SOURCE_FILTERS.map((s) => (
          <button key={s.value} data-active={sourceFilter === s.value} onClick={() => setSourceFilter(s.value)}>
            {s.label}
          </button>
        ))}
      </div>

      {data === null && <div className="node-detail-note">Loading…</div>}
      {data === "unavailable" && <div className="node-detail-note">Packet data didn't respond as expected.</div>}

      {Array.isArray(data) && (
        <div className="node-list-table-wrap">
          <table className="node-list-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Protocol</th>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Source</th>
                <th>SNR / RSSI</th>
                <th>Trust tier</th>
              </tr>
            </thead>
            <tbody>
              {packets.map((p) => {
                const cat = packetCategoryInfo(p.protocol, p.type);
                const protoLabel = isProtocol(p.protocol) ? PROTOCOL_LABEL[p.protocol] : p.protocol;
                const tier = trustTierInfo(p.trust_tier);
                const sources = p.sources ?? [];
                return (
                  <tr key={p.id} className="node-list-row" onClick={() => select({ kind: "packet", packet: p })}>
                    <td>{relativeTime(p.ts)}</td>
                    <td>{protoLabel}</td>
                    <td>
                      <span className="badge" style={{ background: colorFor(cat.color, effectiveTheme) }}>
                        {cat.label}
                      </span>
                    </td>
                    <td title={p.from_id ? String(p.from_id) : undefined}>{p.sender_name || p.from_id || EMPTY}</td>
                    <td>{p.to_id ?? EMPTY}</td>
                    <td>
                      {sources.length === 0
                        ? EMPTY
                        : sources.map((s, i) => (
                            <div key={`${s.kind}-${s.label}-${i}`} className="packet-source-chip">
                              {sourceChipText(s)}
                            </div>
                          ))}
                    </td>
                    <td>
                      {p.snr ?? EMPTY} dB / {p.rssi ?? EMPTY} dBm
                    </td>
                    <td>
                      <span className="badge" style={{ background: colorFor(tier.color, effectiveTheme) }}>
                        {tier.short}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {packets.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty-state">
                    {healthEverLoaded && availableProtocols.length === 0 ? NO_CONNECTIONS_EMPTY_STATE : "No packets match the current filters."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {Array.isArray(data) && packets.length > 0 && (
        <PagingBar rangeStart={rangeStart} rangeEnd={rangeEnd} hasPrev={hasPrev} hasNext={hasNext} onPrev={prev} onNext={next} />
      )}
    </div>
  );
}
